import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Generic admin CRUD for institutions / majors / roadmaps.
 * Route: /admin/catalog/:entity  where entity ∈ institutions|majors|roadmaps
 */
const ENTITY_CONFIG = {
  institutions: {
    label: "Institutions",
    listPath: "/institutions",
    adminBase: "/admin/institutions",
    columns: ["name", "type", "slug", "verification_status", "_placeholder"],
    newTemplate: () => ({ slug: "", name: "", type: "community_college", website: "", body_markdown: "", official_source_url: "", _placeholder: true }),
    editableFields: [
      { key: "name", label: "Name", type: "text" },
      { key: "slug", label: "Slug", type: "text", disabledOnEdit: true },
      { key: "type", label: "Type", type: "select", options: ["community_college", "state_university", "private_university"] },
      { key: "website", label: "Website", type: "text" },
      { key: "official_source_url", label: "Official source URL", type: "text" },
      { key: "body_markdown", label: "Body (markdown)", type: "textarea" },
      { key: "_placeholder", label: "Placeholder (hides from sitemap)", type: "checkbox" },
    ],
  },
  majors: {
    label: "Majors",
    listPath: "/majors",
    adminBase: "/admin/majors",
    columns: ["name", "slug", "limited_access", "verification_status", "_placeholder"],
    newTemplate: () => ({ slug: "", name: "", limited_access: false, body_markdown: "", official_source_url: "", _placeholder: true }),
    editableFields: [
      { key: "name", label: "Name", type: "text" },
      { key: "slug", label: "Slug", type: "text", disabledOnEdit: true },
      { key: "limited_access", label: "Limited-access major", type: "checkbox" },
      { key: "gpa_considerations", label: "GPA considerations", type: "textarea" },
      { key: "official_source_url", label: "Official source URL", type: "text" },
      { key: "body_markdown", label: "Body (markdown)", type: "textarea" },
      { key: "_placeholder", label: "Placeholder (hides from sitemap)", type: "checkbox" },
    ],
  },
  roadmaps: {
    label: "Roadmaps",
    listPath: "/roadmaps",
    adminBase: "/admin/roadmaps",
    columns: ["title", "slug", "verification_status", "_placeholder"],
    newTemplate: () => ({ slug: "", title: "", from_institution_id: "", to_institution_id: "", stages: [], body_markdown: "", _placeholder: true }),
    editableFields: [
      { key: "title", label: "Title", type: "text" },
      { key: "slug", label: "Slug", type: "text", disabledOnEdit: true },
      { key: "from_institution_id", label: "From institution ID", type: "text" },
      { key: "to_institution_id", label: "To institution ID", type: "text" },
      { key: "major_id", label: "Major ID (optional)", type: "text" },
      { key: "body_markdown", label: "Body (markdown)", type: "textarea" },
      { key: "_placeholder", label: "Placeholder (hides from sitemap)", type: "checkbox" },
    ],
  },
};

export default function AdminCatalog() {
  const { entity } = useParams();
  const cfg = ENTITY_CONFIG[entity];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // slug or "__new__"
  const [form, setForm] = useState({});
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api.get(cfg.listPath, { params: { page_size: 200 } })
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (!cfg) return;
    load();
  }, [entity]);

  if (!cfg) {
    return <div className="text-crimson">Unknown entity type. Valid: institutions / majors / roadmaps.</div>;
  }

  const startNew = () => { setEditing("__new__"); setForm(cfg.newTemplate()); };
  const startEdit = (row) => { setEditing(row.slug); setForm({ ...row }); };
  const cancel = () => { setEditing(null); setForm({}); };
  const save = async () => {
    try {
      if (editing === "__new__") {
        await api.post(cfg.adminBase, form);
        toast.success("Created");
      } else {
        const patch = { ...form };
        delete patch.slug;
        await api.patch(`${cfg.adminBase}/${editing}`, patch);
        toast.success("Saved");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const del = async (slug) => {
    if (!window.confirm(`Delete ${slug}?`)) return;
    try { await api.delete(`${cfg.adminBase}/${slug}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid={`admin-${entity}-page`}>
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl text-ivy tracking-tight">{cfg.label}</h1>
          <p className="text-sm text-stone mt-1">{items.length} records</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex border border-stone/40">
            {Object.keys(ENTITY_CONFIG).map((e) => (
              <button
                key={e}
                onClick={() => navigate(`/admin/catalog/${e}`)}
                className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] transition-colors ${e === entity ? "bg-ivy text-bone" : "text-ink hover:bg-stone/10"}`}
                data-testid={`admin-catalog-tab-${e}`}
              >
                {ENTITY_CONFIG[e].label}
              </button>
            ))}
          </div>
          <button onClick={startNew} className="bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-[11px] font-semibold py-2 px-4 transition-colors" data-testid="admin-catalog-new">+ New</button>
        </div>
      </div>

      {loading ? <p className="text-sm text-stone">Loading…</p> : (
        <div className="border border-stone/30 bg-bone">
          <table className="w-full text-sm">
            <thead className="bg-bone-dim/40 text-[10px] uppercase tracking-[0.18em] text-brass">
              <tr>
                {cfg.columns.map((c) => <th key={c} className="text-left px-4 py-2.5 font-semibold">{c.replace(/_/g, " ")}</th>)}
                <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.slug} className="border-t border-stone/20 hover:bg-bone-dim/20">
                  {cfg.columns.map((c) => (
                    <td key={c} className="px-4 py-2.5 text-ink-soft">
                      {typeof row[c] === "boolean" ? (row[c] ? "yes" : "no") : (row[c] || "—")}
                    </td>
                  ))}
                  <td className="px-4 py-2.5 text-right space-x-3">
                    <button onClick={() => startEdit(row)} className="text-[11px] uppercase tracking-[0.18em] text-ivy hover:text-brass" data-testid={`admin-edit-${row.slug}`}>Edit</button>
                    <a href={`${entity === "institutions" ? (row.type === "community_college" ? "/community-colleges" : "/universities") : `/${entity}`}/${row.slug}`} target="_blank" rel="noopener noreferrer" className="text-[11px] uppercase tracking-[0.18em] text-stone hover:text-ivy">View</a>
                    <button onClick={() => del(row.slug)} className="text-[11px] uppercase tracking-[0.18em] text-crimson hover:text-crimson/70">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-ink/70 z-50 flex items-start justify-center py-10 px-4 overflow-y-auto" data-testid="admin-catalog-modal">
          <div className="bg-bone border border-stone/40 w-full max-w-3xl p-6 lg:p-8">
            <div className="flex items-baseline justify-between mb-6">
              <h2 className="font-serif text-2xl text-ivy tracking-tight">{editing === "__new__" ? `New ${cfg.label.slice(0, -1)}` : `Edit ${form.name || form.title || form.slug}`}</h2>
              <button onClick={cancel} className="text-stone hover:text-ink text-2xl">×</button>
            </div>
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              {cfg.editableFields.map((f) => {
                const disabled = editing !== "__new__" && f.disabledOnEdit;
                return (
                  <div key={f.key}>
                    <label className="block text-[10px] uppercase tracking-[0.22em] text-brass font-semibold mb-2">{f.label}</label>
                    {f.type === "textarea" ? (
                      <textarea
                        rows={f.key === "body_markdown" ? 12 : 3}
                        value={form[f.key] || ""}
                        disabled={disabled}
                        onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                        className="w-full border border-stone/40 bg-bone px-3 py-2 text-sm font-mono resize-y focus:border-ivy outline-none disabled:opacity-50"
                        data-testid={`admin-field-${f.key}`}
                      />
                    ) : f.type === "select" ? (
                      <select
                        value={form[f.key] || ""}
                        disabled={disabled}
                        onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                        className="w-full border border-stone/40 bg-bone px-3 py-2 text-sm focus:border-ivy outline-none disabled:opacity-50"
                        data-testid={`admin-field-${f.key}`}
                      >
                        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : f.type === "checkbox" ? (
                      <label className="flex items-center gap-2 text-sm text-ink">
                        <input type="checkbox" checked={!!form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })} className="h-4 w-4" data-testid={`admin-field-${f.key}`} />
                        <span>{f.label}</span>
                      </label>
                    ) : (
                      <input
                        type="text"
                        value={form[f.key] || ""}
                        disabled={disabled}
                        onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                        className="w-full border border-stone/40 bg-bone px-3 py-2 text-sm focus:border-ivy outline-none disabled:opacity-50"
                        data-testid={`admin-field-${f.key}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-6 pt-4 border-t border-stone/30 flex justify-end gap-3">
              <button onClick={cancel} className="text-[11px] uppercase tracking-[0.18em] text-stone hover:text-ink">Cancel</button>
              <button onClick={save} className="bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-[11px] font-semibold py-2 px-5" data-testid="admin-catalog-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
